import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useFormContext, useWatch } from 'react-hook-form';
import { FaTimes } from 'react-icons/fa';

import { DetailLink } from 'components/DetailsView/DetailLink';
import { BundleFormData } from '../constants/types';

export const BoxProductTile = ({ boxIndex, productIndex }: { boxIndex: number; productIndex: number }) => {
    const { register, setValue, watch, control } = useFormContext<BundleFormData>();

    // Surveiller toute la structure boxes pour que useWatch se mette à jour quand les indices changent
    const boxes = useWatch({ control, name: 'boxes' });
    const productData = boxes?.[boxIndex]?.products?.[productIndex];

    const handleDelete = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            const products = watch(`boxes.${boxIndex}.products`);
            setValue(
                `boxes.${boxIndex}.products`,
                products.filter((_, index) => index !== productIndex),
                { shouldDirty: true },
            );
        },
        [boxIndex, productIndex, setValue, watch],
    );

    if (!productData) {
        return null;
    }

    const { photoUrl, location, productLabel, quantity, quantityPicked, _id: productId } = productData;

    const isQuantityValid =
        Number(quantity) === Number(quantityPicked) && !isNaN(Number(quantity)) && !isNaN(Number(quantityPicked));

    return (
        <ProductContainer>
            <ProductImageContainer>
                <QuantityInputBadge>
                    <QuantityInput
                        $isQuantityValid={isQuantityValid}
                        type="number"
                        min={0}
                        placeholder="-"
                        aria-label="Quantité ramassée"
                        {...register(`boxes.${boxIndex}.products.${productIndex}.quantityPicked`)}
                    />
                    <SlashSeparator>/</SlashSeparator>
                    <QuantityInput
                        $isQuantityValid={isQuantityValid}
                        type="number"
                        min={0}
                        placeholder="-"
                        aria-label="Quantité demandée"
                        {...register(`boxes.${boxIndex}.products.${productIndex}.quantity`)}
                    />
                </QuantityInputBadge>
                {photoUrl && <ProductImage src={photoUrl} alt={productLabel} />}
            </ProductImageContainer>

            <ProductDetails>
                <ProductNameContainer>
                    <ProductName>
                        <DetailLink path="product" value={productId} name={productLabel} color="black" shouldWrap />
                    </ProductName>
                    <DeleteButton onClick={handleDelete}>
                        <FaTimes />
                    </DeleteButton>
                </ProductNameContainer>
                {location && <ProductLocation>{location}</ProductLocation>}
            </ProductDetails>
            <QuantityToPick>
                <QuantityLabel>Quantité</QuantityLabel>
                <QuantityValue>{quantity}</QuantityValue>
            </QuantityToPick>
        </ProductContainer>
    );
};

const ProductContainer = styled.div`
    display: flex;
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    overflow: hidden;
    background-color: transparent;
    width: 100%;
`;

const ProductImageContainer = styled.div`
    position: relative;
    min-width: 100px;
`;

const ProductImage = styled.img`
    width: 100px;
    height: 100px;
    object-fit: contain;

    @media print {
        display: block;
    }

    @media print {
        display: none;
    }
`;

const QuantityInputBadge = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius};
    margin-bottom: 6px;
    width: fit-content;
    margin-left: auto;
    margin-right: auto;
`;

const QuantityInput = styled.input<{ $isQuantityValid: boolean }>`
    width: 100%;
    height: 22px;
    font-size: 14px;
    border: 1px solid ${({ theme }) => theme.ctaSecondaryBorderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    text-align: center;
    background-color: ${({ $isQuantityValid, theme }) =>
        $isQuantityValid ? `${theme.successColor}CC` : `${theme.warningColor}CC`};
    outline: none;
    font-weight: bold;
    /* Remove arrows for number input */
    appearance: textfield;
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
`;

const ProductDetails = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 10px;
    border-left: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const ProductNameContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

const ProductName = styled.span`
    font-size: 18px;
`;

const ProductLocation = styled.span``;

const QuantityToPick = styled.div`
    flex-direction: column;
    align-items: center;
    padding: 5px;

    @media screen {
        display: none;
    }

    @media print {
        display: flex;
    }
`;

const QuantityLabel = styled.span``;

const QuantityValue = styled.span`
    font-size: 36px;
`;

const SlashSeparator = styled.span`
    font-size: 13px;
    margin: 0 2px;
    font-weight: bold;
`;

const DeleteButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.errorColor};
    cursor: pointer;
    padding: 4px;
    font-size: 16px;
    border-radius: ${({ theme }) => theme.borderRadius};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 8px;

    &:hover {
        background-color: ${({ theme }) => `${theme.errorColor}20`};
    }
`;
